import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Instagram, Facebook } from 'lucide-react';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { areas } from '../data/areas';
import { getAllServiceSlugs, getServiceBySlug } from '../data/services';
import { treatments, TREATMENT_AREA_SLUGS } from '../data/treatmentAreas';
import { SEDATION_NAV } from '../data/sedation';

const primaryPages = [
  { to: '/dentist-acton', label: 'General Dentist Acton' },
  { to: '/invisalign-acton', label: 'Invisalign Acton' },
  { to: '/implants-acton', label: 'Dental Implants Acton' },
  { to: '/composite-bonding-acton', label: 'Composite Bonding Acton' },
  { to: '/cosmetic-dentist-acton', label: 'Cosmetic Dentist Acton' },
  { to: '/emergency-dentist-acton', label: 'Emergency Dentist Acton' },
];

const practicePages = [
  { to: '/about-dr-kartik-patel', label: 'About Dr Kartik Patel' },
  { to: '/reviews', label: 'Patient Reviews' },
  { to: '/before-after', label: 'Before & After Gallery' },
  { to: '/pricing', label: 'Fees & Prices' },
  { to: '/finance', label: '0% Dental Finance' },
  { to: '/contact', label: 'Contact & Directions' },
  { to: '/enquiry', label: 'Make an Enquiry' },
  { to: '/patient-questions', label: 'Patient Q&A' },
  { to: '/knowledge-base', label: 'Knowledge Base' },
];

const humanise = (slug) => slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()).replace('Tmj', 'TMJ').replace('All On 4', 'All-on-4');

/** "Areas we serve" — every treatment × area page (e.g. /invisalign-ealing), grouped by area, so none is orphaned. */
export const AreaTreatmentLinks = ({ headingClass = 'text-base font-semibold mb-4 text-amberlight' }) => (
  <nav aria-label="Treatments by area" data-testid="footer-areas-we-serve">
    <h2 className={headingClass}>Areas We Serve — Treatments Near You</h2>
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5 text-sm">
      {areas.map((a) => (
        <div key={a.slug}>
          <Link to={`/dentist-${a.slug}`} className="font-semibold text-white hover:text-burnt-coral">{a.name} <span className="text-white/60 font-normal">({a.postcode})</span></Link>
          <ul className="mt-2 space-y-1 text-white/75">
            {TREATMENT_AREA_SLUGS.map((t) => (
              <li key={t}><Link to={`/${t}-${a.slug}`} className="hover:text-white" data-testid={`footer-link-${t}-${a.slug}`}>{treatments[t].name} {a.name}</Link></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </nav>
);

/** The four crawlable link columns (treatments, all services, areas, practice). */
export const SiteLinkColumns = ({ headingClass = 'text-base font-semibold mb-4 text-amberlight' }) => {
  const serviceSlugs = getAllServiceSlugs();
  return (
    <>
      <nav aria-label="Popular treatments">
        <h2 className={headingClass}>Treatments in Acton</h2>
        <ul className="space-y-2 text-sm text-white/80">
          {primaryPages.map((p) => <li key={p.to}><Link to={p.to} className="hover:text-white">{p.label}</Link></li>)}
        </ul>
        <h2 className={`${headingClass} mt-7`}>Sedation</h2>
        <ul className="space-y-2 text-sm text-white/80" data-testid="footer-sedation-links">
          {SEDATION_NAV.map((p) => <li key={p.to}><Link to={p.to} className="hover:text-white" data-testid={`footer-link${p.to.replace(/\//g, '-')}`}>{p.label}</Link></li>)}
        </ul>
      </nav>

      <nav aria-label="All treatments">
        <h2 className={headingClass}>All Treatments</h2>
        <ul className="space-y-2 text-sm text-white/80 columns-1">
          {serviceSlugs.map((slug) => {
            const s = getServiceBySlug(slug);
            return <li key={slug}><Link to={`/services/${slug}`} className="hover:text-white">{s?.shortName || humanise(slug)}</Link></li>;
          })}
        </ul>
      </nav>

      <nav aria-label="Areas we serve">
        <h2 className={headingClass}>Dentist Near You</h2>
        <ul className="space-y-2 text-sm text-white/80">
          {areas.map((a) => <li key={a.slug}><Link to={`/dentist-${a.slug}`} className="hover:text-white">Dentist {a.name} ({a.postcode})</Link></li>)}
        </ul>
      </nav>

      <nav aria-label="Practice information">
        <h2 className={headingClass}>The Practice</h2>
        <ul className="space-y-2 text-sm text-white/80">
          {practicePages.map((p) => <li key={p.to}><Link to={p.to} className="hover:text-white">{p.label}</Link></li>)}
          <li><a href={PRACTICE.links.googleBusinessProfile} target="_blank" rel="noopener noreferrer" className="hover:text-white">Review us on Google</a></li>
        </ul>
      </nav>
    </>
  );
};

/**
 * Site-wide footer: NAP block + crawlable links to every public page.
 * Used on every page so no page is orphaned for crawlers.
 */
const SiteFooter = () => {
  return (
    <footer className="bg-rabbit text-white" data-testid="site-footer">
      <div className="container mx-auto px-4 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-1" itemScope itemType="https://schema.org/Dentist">
            <p className="text-xl font-bold mb-3" itemProp="name">{PRACTICE.name}</p>
            <p className="text-white/70 text-sm mb-4">{PRACTICE.dentistName} {PRACTICE.dentistQualifications} · GDC {PRACTICE.gdcNumber}</p>
            <address className="not-italic text-sm text-white/80 space-y-2" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
              <p className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-burnt-coral flex-shrink-0" aria-hidden="true" />
                <span><span itemProp="streetAddress">{PRACTICE.address.street}</span>, <span itemProp="addressLocality">{PRACTICE.address.locality}</span>, <span itemProp="addressRegion">{PRACTICE.address.region}</span> <span itemProp="postalCode">{PRACTICE.address.postcode}</span></span>
              </p>
            </address>
            <p className="flex gap-2 text-sm text-white/80 mt-2"><Phone className="w-4 h-4 mt-0.5 text-burnt-coral flex-shrink-0" aria-hidden="true" />
              <a href={PRACTICE.phoneTel} className="hover:text-white" itemProp="telephone" data-testid="footer-phone-link">{PRACTICE.phoneDisplay}</a>
            </p>
            <p className="flex gap-2 text-sm text-white/80 mt-2"><Clock className="w-4 h-4 mt-0.5 text-burnt-coral flex-shrink-0" aria-hidden="true" />
              <span>Mon–Fri 9:00–18:00 · Sat 9:00–14:00</span>
            </p>
            <div className="flex gap-3 mt-4">
              <a href={PRACTICE.links.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-burnt-coral"><Instagram className="w-5 h-5" /></a>
              <a href={PRACTICE.links.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-burnt-coral"><Facebook className="w-5 h-5" /></a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="text-sm underline underline-offset-4 hover:text-burnt-coral" data-testid="footer-whatsapp-link">WhatsApp us</a>
            </div>
          </div>

          <SiteLinkColumns />
        </div>

        <div className="border-t border-white/10 mt-12 pt-10">
          <AreaTreatmentLinks />
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-xs text-white/60 flex flex-col md:flex-row gap-2 md:justify-between">
          <p>© {new Date().getFullYear()} {PRACTICE.name} · {PRACTICE.dentistName} · GDC Registration {PRACTICE.gdcNumber}</p>
          <p>Private dentist in Acton serving Ealing, Chiswick, Hammersmith and West London.</p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
